import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import Solutions from './pages/Solutions';
import Contact from './pages/Contact';
import ClientLogin from './pages/ClientLogin';
import VerifyCode from './pages/VerifyCode';
import Dashboard from './pages/Dashboard';
import API_BASE_URL from './config/api';
import './index.css';

function SessionGate({ children, require2FA = false, blockWhenLoggedIn = false }) {
  const [checking, setChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [is2FAVerified, setIs2FAVerified] = useState(false);

  useEffect(() => {
    const clearSession = () => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      localStorage.removeItem('is2FAVerified');
      setIsAuthenticated(false);
      setIs2FAVerified(false);
    };

    const verifyAccessToken = async (token) => {
      const response = await fetch(`${API_BASE_URL}/api/verify-token`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!(response.ok && data.status === 'success')) {
        throw new Error(data.message || 'Token verification failed');
      }

      localStorage.setItem('username', data.username || '');
      localStorage.setItem('role', data.role || 'guest');
      localStorage.setItem('is2FAVerified', data.is2FAVerified ? 'true' : 'false');

      setIsAuthenticated(true);
      setIs2FAVerified(Boolean(data.is2FAVerified));
    };

    const tryRefresh = async () => {
      const response = await fetch(`${API_BASE_URL}/api/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (!(response.ok && data.status === 'success')) {
        throw new Error(data.message || 'Refresh failed');
      }

      localStorage.setItem('authToken', data.accessToken);
      localStorage.setItem('username', data.username || '');
      localStorage.setItem('role', data.role || 'guest');
      localStorage.setItem('is2FAVerified', data.is2FAVerified ? 'true' : 'false');

      setIsAuthenticated(true);
      setIs2FAVerified(Boolean(data.is2FAVerified));
    };

    const checkSession = async () => {
      try {
        const token = localStorage.getItem('authToken');

        if (token) {
          try {
            await verifyAccessToken(token);
            return;
          } catch (error) {
            console.warn('Access token invalid, trying refresh...', error);
          }
        }

        await tryRefresh();
      } catch (error) {
        clearSession();
      } finally {
        setChecking(false);
      }
    };

    checkSession();
  }, []);

  if (checking) {
    return (
      <div className="login-page-wrap">
        <div className="login-card">
          <p className="login-label">SESSION</p>
          <h1>Checking session...</h1>
          <p className="login-subtext">
            Please wait while we verify your login state.
          </p>
        </div>
      </div>
    );
  }

  // 로그인된 사용자가 /login 접근 시 자동 이동
  if (blockWhenLoggedIn && isAuthenticated) {
    if (is2FAVerified) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/verify" replace />;
  }

  // 인증 필요한 페이지
  if (!blockWhenLoggedIn && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 2FA까지 필요한 페이지
  if (require2FA && !is2FAVerified) {
    return <Navigate to="/verify" replace />;
  }

  return children;
}

function AppContent() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const isOverlayPage =
    location.pathname !== '/' &&
    location.pathname !== '/dashboard' &&
    location.pathname !== '/verify';
  const isHomeRoute = location.pathname === '/';

  return (
    <div className="app-layout">
      <Home />

      {isOverlayPage && <div className="background-blur-layer" />}

      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`page-area ${isHomeRoute ? 'page-area-home' : ''}`}>
        <Routes>
          <Route path="/" element={null} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/contact" element={<Contact />} />

          <Route
            path="/login"
            element={
              <SessionGate blockWhenLoggedIn={true}>
                <ClientLogin />
              </SessionGate>
            }
          />

          <Route
            path="/verify"
            element={
              <SessionGate>
                <VerifyCode />
              </SessionGate>
            }
          />

          <Route
            path="/dashboard"
            element={
              <SessionGate require2FA={true}>
                <Dashboard />
              </SessionGate>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
